import { FormControl, FormHelperText, FormLabel, RadioGroup, RadioGroupProps } from "@mui/material";
import { Controller, FieldValues, Path, useFormContext, Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

type FormRadioGroupProps<IFormValues extends FieldValues = FieldValues> = Omit<RadioGroupProps, 'value'> & {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    label?: string,
    readOnly?: boolean,
    required?: boolean,
    disabled?: boolean,
    children: JSX.Element | JSX.Element[]
}

const FormRadioGroup = <IFormValues extends FieldValues = FieldValues>({
    name,
    control,
    label,
    readOnly = false,
    required = false,
    disabled,
    children,
    ...props
}: FormRadioGroupProps<any>) => {
    const {t} = useTranslation(['network', 'translation']);
    const formContext = useFormContext<IFormValues>();

    return (
        <Controller
            name={name}
            control={control ?? formContext.control}
            render={({ field, fieldState, formState }) =>
                <FormControl error={!!fieldState.error} required={required} disabled={!!disabled} sx={{width: '100%'}}>
                    {label && <FormLabel>{label}</FormLabel>}
                    <RadioGroup
                        ref={field.ref}
                        {...props}
                        value={field.value}
                        onChange={(e, value) => {
                            if (!readOnly && !formState.isSubmitting) {
                                field.onChange(value);
                                props.onChange?.(e, value);
                            }
                        }}
                        onBlur={field.onBlur}
                        row={props.row ?? true}
                    >
                        {children}
                    </RadioGroup>
                    {t(!!fieldState.error?.message ? fieldState.error.message : '') && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                </FormControl>
            }
        />
    )
};

export default FormRadioGroup;